/**
 * Reforma Tributária (CBS + IBS + IS) — LC 214/2025, fase-teste 2026.
 * Página do menu Fiscal. Isolada do emissor NF-e (parametrização + cálculo).
 * Abas: Configuração · Classificação de produtos · Tabelas oficiais · Simulador · Apuração.
 *
 * [30/07/2026] Duas adições:
 * - aba "Tabelas": as tabelas CST/cClassTrib do IT 2025.002 mudam a cada versão do
 *   Informe Técnico (já está na v1.50), então precisam ser editáveis e recarregáveis
 *   direto da fonte oficial (Portal SVRS) sem depender de deploy;
 * - botão "Sugerir por NCM" na aba Classificação: dry-run que propõe o cClassTrib pelos
 *   Anexos I e VII da LC 214/2025 e só grava o que o usuário marcar.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import {
  Calculator, Settings, Tags, FileBarChart, Plus, Trash2, Save, Search, Loader2,
  Table2, RefreshCw, Wand2, AlertTriangle, ExternalLink, Check, X, GitBranch, Power,
} from 'lucide-react'

type Cst = { codigo: string; descricao: string; tributado: boolean; grupo?: string | null }
type ClassTrib = {
  codigo: string; cst: string; descricao: string; reducao_pct: number | null
  permite_credito?: boolean; ativo?: boolean
  ind_trib_regular?: boolean; permite_cred_pres?: boolean
  nro_anexo?: string | null; url_legislacao?: string | null
}

const TABS = [
  { id: 'config', label: 'Configuração', icon: Settings },
  { id: 'produtos', label: 'Classificação', icon: Tags },
  { id: 'operacoes', label: 'Operações', icon: GitBranch },
  { id: 'tabelas', label: 'Tabelas oficiais', icon: Table2 },
  { id: 'simulador', label: 'Simulador', icon: Calculator },
  { id: 'apuracao', label: 'Apuração', icon: FileBarChart },
] as const

export default function ReformaTributaria() {
  const [tab, setTab] = useState<typeof TABS[number]['id']>('config')
  return (
    <div className="p-5 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-ink-900">Reforma Tributária · CBS / IBS / IS</h1>
        <p className="text-sm text-ink-500">Parametrização e cálculo dos novos tributos (LC 214/2025). Fase-teste 2026 — sem valor fiscal, isolado do emissor de NF-e.</p>
      </div>
      <div className="flex gap-1 border-b border-ink-200">
        {TABS.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? 'border-brand-600 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'}`}>
              <Icon size={15} /> {t.label}
            </button>
          )
        })}
      </div>
      {tab === 'config' && <AbaConfig />}
      {tab === 'produtos' && <AbaProdutos />}
      {tab === 'operacoes' && <AbaOperacoes />}
      {tab === 'tabelas' && <AbaTabelas />}
      {tab === 'simulador' && <AbaSimulador />}
      {tab === 'apuracao' && <AbaApuracao />}
    </div>
  )
}

// ======================= CONFIGURAÇÃO =======================
function AbaConfig() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['reforma-config'], queryFn: async () => (await api.get('/reforma/config')).data })
  const [form, setForm] = useState<any>(null)
  const cfg = form ?? data
  const set = (k: string, v: any) => setForm({ ...(form ?? data), [k]: v })

  const save = useMutation({
    mutationFn: async () => (await api.put('/reforma/config', {
      fase: cfg.fase, ativo: cfg.ativo,
      cbs_pct: Number(cfg.cbs_pct), ibs_uf_pct: Number(cfg.ibs_uf_pct), ibs_mun_pct: Number(cfg.ibs_mun_pct),
      is_pct: Number(cfg.is_pct), cst_padrao: cfg.cst_padrao, cclasstrib_padrao: cfg.cclasstrib_padrao,
      compensa_pis_cofins: cfg.compensa_pis_cofins, emitir_no_xml: cfg.emitir_no_xml,
      vigencia_inicio: cfg.vigencia_inicio || null,
      observacao: cfg.observacao || null,
    })).data,
    onSuccess: () => { toast.success('Configuração salva!'); qc.invalidateQueries({ queryKey: ['reforma-config'] }); setForm(null) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  if (isLoading || !cfg) return <Loading />
  const ibsTotal = (Number(cfg.ibs_uf_pct) + Number(cfg.ibs_mun_pct)).toFixed(4).replace(/\.?0+$/, '')

  return (
    <div className="card"><div className="card-body space-y-4 max-w-2xl">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        💡 Fase-teste 2026: <strong>CBS 0,9%</strong> + <strong>IBS 0,1%</strong> (UF + Município), cálculo <strong>por fora</strong>.
        Os valores podem ser compensados com PIS/COFINS no mesmo período (sem carga extra).
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">Fase</label>
          <select className="input" value={cfg.fase} onChange={e => set('fase', e.target.value)}>
            <option value="teste">Teste (2026)</option>
            <option value="transicao">Transição (2027–2032)</option>
            <option value="pleno">Pleno (2033+)</option>
          </select>
        </div>
        <div>
          <label className="label">Vigência início</label>
          <input className="input" type="date" value={cfg.vigencia_inicio || ''} onChange={e => set('vigencia_inicio', e.target.value)} />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfg.ativo} onChange={e => set('ativo', e.target.checked)} /> Ativo</label>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <NumField label="CBS %" value={cfg.cbs_pct} onChange={v => set('cbs_pct', v)} />
        <NumField label="IBS UF %" value={cfg.ibs_uf_pct} onChange={v => set('ibs_uf_pct', v)} />
        <NumField label="IBS Mun. %" value={cfg.ibs_mun_pct} onChange={v => set('ibs_mun_pct', v)} />
        <NumField label="IS %" value={cfg.is_pct} onChange={v => set('is_pct', v)} />
      </div>
      <div className="text-xs text-ink-500">IBS total: <strong>{ibsTotal}%</strong> · Carga combinada CBS+IBS: <strong>{(Number(cfg.cbs_pct) + Number(cfg.ibs_uf_pct) + Number(cfg.ibs_mun_pct)).toFixed(2)}%</strong></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">CST padrão</label>
          <input className="input font-mono" maxLength={3} value={cfg.cst_padrao} onChange={e => set('cst_padrao', e.target.value)} />
        </div>
        <div>
          <label className="label">cClassTrib padrão</label>
          <input className="input font-mono" maxLength={6} value={cfg.cclasstrib_padrao} onChange={e => set('cclasstrib_padrao', e.target.value)} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfg.compensa_pis_cofins} onChange={e => set('compensa_pis_cofins', e.target.checked)} /> Compensar CBS de teste com PIS/COFINS (2026)</label>

      {/* [30/07/2026] Gate do grupo IBS/CBS no XML. CRT 3 (caso da Mauriti) tem
          obrigatoriedade em produção em 03/08/2026 — depois disso a SEFAZ rejeita
          nota SEM os grupos. Antes de ligar, validar em HOMOLOGAÇÃO. */}
      <div className={`rounded-lg border p-3 ${cfg.emitir_no_xml ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!cfg.emitir_no_xml} onChange={e => set('emitir_no_xml', e.target.checked)} />
          Emitir o grupo IBS/CBS no XML da NF-e
        </label>
        <p className="text-xs mt-1.5 text-ink-700">
          {cfg.emitir_no_xml
            ? 'LIGADO — as próximas notas sairão com os grupos IBSCBS (item) e IBSCBSTot (total).'
            : 'DESLIGADO — as notas saem como hoje, sem os grupos.'}
        </p>
        <p className="text-xs mt-1 text-red-800">
          <strong>Prazo:</strong> para CRT 3 (regime normal) os grupos são obrigatórios em produção
          desde <strong>03/08/2026</strong>. Sem eles a SEFAZ rejeita a nota. Valide em
          <strong> homologação</strong> antes de ligar em produção.
        </p>
      </div>
      <div>
        <label className="label">Observação</label>
        <textarea className="input" rows={2} value={cfg.observacao || ''} onChange={e => set('observacao', e.target.value)} />
      </div>
      <button className="btn-primary" disabled={!form || save.isPending} onClick={() => save.mutate()}>
        <Save size={15} /> Salvar configuração
      </button>
    </div></div>
  )
}

// ======================= CLASSIFICAÇÃO POR PRODUTO =======================
function AbaProdutos() {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [semClass, setSemClass] = useState(false)
  const [sugerindo, setSugerindo] = useState(false)
  const { data: cst } = useQuery<Cst[]>({ queryKey: ['reforma-cst'], queryFn: async () => (await api.get('/reforma/cst')).data })
  const { data: ct } = useQuery<ClassTrib[]>({ queryKey: ['reforma-classtrib'], queryFn: async () => (await api.get('/reforma/classtrib')).data })
  const { data: produtos, isLoading } = useQuery({
    queryKey: ['reforma-produtos', busca, semClass],
    queryFn: async () => (await api.get('/reforma/produtos', { params: { busca: busca || undefined, sem_classificacao: semClass, limit: 100 } })).data,
  })

  const salvar = useMutation({
    mutationFn: async (p: any) => (await api.put(`/reforma/produtos/${p.produto_id}`, { cst: p.cst, cclasstrib: p.cclasstrib, reducao_pct: p.reducao_pct })).data,
    onSuccess: () => { toast.success('Produto classificado'); qc.invalidateQueries({ queryKey: ['reforma-produtos'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  if (sugerindo) return <PainelSugestao onFechar={() => setSugerindo(false)} />

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-2.5 top-2.5 text-ink-400" />
          <input className="input pl-8" placeholder="Buscar produto por nome ou código…" value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-600"><input type="checkbox" checked={semClass} onChange={e => setSemClass(e.target.checked)} /> Só não classificados</label>
        <button className="btn-secondary ml-auto" onClick={() => setSugerindo(true)}>
          <Wand2 size={15} /> Sugerir por NCM
        </button>
      </div>
      <div className="card overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-left text-ink-500 border-b border-ink-200">
          <th className="p-2">Produto</th><th className="p-2 w-24">CST</th><th className="p-2 w-32">cClassTrib</th><th className="p-2 w-24">Redução %</th><th className="p-2 w-20"></th>
        </tr></thead>
        <tbody>
          {isLoading && <tr><td colSpan={5} className="p-4 text-center text-ink-400"><Loader2 className="animate-spin inline" /></td></tr>}
          {produtos?.map((p: any) => <LinhaProduto key={p.produto_id} p={p} cst={cst || []} ct={ct || []} onSave={salvar.mutate} saving={salvar.isPending} />)}
          {produtos?.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-ink-400">Nenhum produto.</td></tr>}
        </tbody>
      </table></div>
    </div>
  )
}

function LinhaProduto({ p, cst, ct, onSave, saving }: any) {
  const [row, setRow] = useState({ ...p })
  const dirty = row.cst !== p.cst || row.cclasstrib !== p.cclasstrib || row.reducao_pct !== p.reducao_pct
  const ctFiltrado = ct.filter((c: ClassTrib) => !row.cst || c.cst === row.cst)
  return (
    <tr className="border-b border-ink-100 hover:bg-ink-50">
      <td className="p-2">
        <div className="font-medium text-ink-800">{p.nome}</div>
        <div className="text-xs text-ink-400">{p.codigo}{p.ncm ? ` · NCM ${p.ncm}` : ''}{p.classificado ? '' : ' · (usando padrão)'}</div>
      </td>
      <td className="p-2">
        <select className="input py-1 text-xs font-mono" value={row.cst} onChange={e => setRow({ ...row, cst: e.target.value, cclasstrib: '' })}>
          {cst.map((c: Cst) => <option key={c.codigo} value={c.codigo} title={c.descricao}>{c.codigo} — {c.descricao}</option>)}
        </select>
      </td>
      <td className="p-2">
        {/* Com 97 códigos oficiais o número sozinho não diz nada — mostra a descrição
            e a redução, senão não há como escolher entre 200003 e 200034. */}
        <select className="input py-1 text-xs font-mono" value={row.cclasstrib} onChange={e => setRow({ ...row, cclasstrib: e.target.value })}>
          <option value="">—</option>
          {ctFiltrado.map((c: ClassTrib) => (
            <option key={c.codigo} value={c.codigo} title={c.descricao}>
              {c.codigo}{c.reducao_pct ? ` (−${c.reducao_pct}%)` : ''} — {c.descricao}
            </option>
          ))}
        </select>
      </td>
      <td className="p-2">
        <input className="input py-1 text-xs" type="number" step="0.01" value={row.reducao_pct ?? ''} onChange={e => setRow({ ...row, reducao_pct: e.target.value === '' ? null : Number(e.target.value) })} />
      </td>
      <td className="p-2">
        {dirty && <button className="btn-primary py-1 px-2 text-xs" disabled={saving} onClick={() => onSave(row)}><Save size={13} /></button>}
      </td>
    </tr>
  )
}

// ======================= SUGESTÃO POR NCM (dry-run) =======================
/**
 * Roda o dry-run de /reforma/produtos/sugerir e deixa o usuário marcar o que aplicar.
 * Nada é gravado até clicar em "Aplicar selecionados".
 *
 * Regra de segurança: as linhas com `revisar` (NCM genérico que consta no anexo por um
 * item específico — ex.: 2106.90.90, que no Anexo I é fórmula infantil e no cadastro do
 * Mauriti são 39 estabilizantes/pastas de sorvete) começam DESMARCADAS e exigem ligar o
 * "permitir revisar" pra sequer serem enviadas.
 */
function PainelSugestao({ onFechar }: { onFechar: () => void }) {
  const qc = useQueryClient()
  const [soBeneficio, setSoBeneficio] = useState(true)
  const [sel, setSel] = useState<Set<number>>(new Set())
  const [permitirRevisar, setPermitirRevisar] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['reforma-sugestao', soBeneficio],
    queryFn: async () => (await api.get('/reforma/produtos/sugerir', { params: { somente_com_beneficio: soBeneficio } })).data,
  })

  const aplicar = useMutation({
    mutationFn: async () => (await api.post('/reforma/produtos/aplicar-lote', {
      produto_ids: [...sel], permitir_revisar: permitirRevisar,
    })).data,
    onSuccess: (d: any) => {
      toast.success(`${d.aplicados} produto(s) classificado(s)${d.pulados ? ` · ${d.pulados} pulado(s)` : ''}`)
      if (d.pulados) toast(`${d.pulados} precisam de revisão manual — marque "aplicar também os que exigem revisão" se for intencional.`, { duration: 6000 })
      setSel(new Set())
      qc.invalidateQueries({ queryKey: ['reforma-sugestao'] })
      qc.invalidateQueries({ queryKey: ['reforma-produtos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao aplicar'),
  })

  const itens: any[] = data?.itens || []
  const elegiveis = itens.filter(i => permitirRevisar || !i.revisar)
  const toggle = (id: number) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const marcarTodos = () => setSel(new Set(elegiveis.filter(i => i.muda).map(i => i.produto_id)))

  if (isLoading) return <Loading />
  const r = data?.resumo || {}

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button className="btn-secondary" onClick={onFechar}><X size={15} /> Voltar</button>
        <h3 className="font-semibold text-ink-800">Sugestão de classificação pelo NCM</h3>
        <span className="text-xs text-ink-400">{data?.total_produtos} produtos analisados · nada gravado ainda</span>
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900 space-y-1">
        <div>
          A sugestão sai dos <strong>Anexos I</strong> (alíquota zero) e <strong>VII</strong> (redução de 60%) da LC 214/2025.
          Produto fora dos anexos fica em <strong>tributação integral {data?.default_config?.cclasstrib}</strong> — que já é o padrão da
          configuração, então nem precisa ser gravado.
        </div>
        <div className="flex gap-4 flex-wrap pt-1 font-medium">
          <span>Anexo I (zero): {r.anexo_i ?? 0}</span>
          <span>Anexo VII (60%): {r.anexo_vii ?? 0}</span>
          <span>Integral: {r.integral ?? 0}</span>
          <span className="text-amber-700">Precisam de revisão: {r.revisar ?? 0}</span>
          <span className="text-ink-500">Já classificados: {r.ja_classificado ?? 0}</span>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        <AlertTriangle size={14} className="inline mr-1" />
        O benefício dos anexos depende do <strong>NCM + a descrição do produto</strong>. NCM genérico
        (terminado em 90) costuma constar no anexo por um item específico e arrastaria produto sem direito —
        essas linhas vêm marcadas como <strong>revisar</strong> e não são aplicadas no automático.
        Confirme com a contabilidade antes de gravar.
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input type="checkbox" checked={soBeneficio} onChange={e => { setSoBeneficio(e.target.checked); setSel(new Set()) }} />
          Mostrar só quem tem benefício ou precisa de revisão
        </label>
        <label className="flex items-center gap-2 text-sm text-amber-800">
          <input type="checkbox" checked={permitirRevisar} onChange={e => { setPermitirRevisar(e.target.checked); setSel(new Set()) }} />
          Aplicar também os que exigem revisão
        </label>
        <button className="btn-secondary py-1 px-2 text-xs" onClick={marcarTodos}>Marcar os que mudam ({elegiveis.filter(i => i.muda).length})</button>
        {sel.size > 0 && <button className="btn-secondary py-1 px-2 text-xs" onClick={() => setSel(new Set())}>Limpar seleção</button>}
        <button className="btn-primary ml-auto" disabled={!sel.size || aplicar.isPending} onClick={() => aplicar.mutate()}>
          {aplicar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Aplicar selecionados ({sel.size})
        </button>
      </div>

      <div className="card overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-left text-ink-500 border-b border-ink-200">
          <th className="p-2 w-8"></th><th className="p-2">Produto</th><th className="p-2 w-24">NCM</th>
          <th className="p-2 w-28">Hoje</th><th className="p-2 w-32">Sugerido</th><th className="p-2">Por quê</th>
        </tr></thead>
        <tbody>
          {itens.map(i => {
            const bloqueado = i.revisar && !permitirRevisar
            return (
              <tr key={i.produto_id} className={`border-b border-ink-100 ${i.revisar ? 'bg-amber-50/50' : i.muda ? 'bg-emerald-50/40' : ''}`}>
                <td className="p-2">
                  <input type="checkbox" disabled={bloqueado} checked={sel.has(i.produto_id)} onChange={() => toggle(i.produto_id)} />
                </td>
                <td className="p-2">
                  <div className="font-medium text-ink-800">{i.nome}</div>
                  <div className="text-xs text-ink-400">
                    {i.codigo}{!i.ativo && ' · inativo'}{i.ja_classificado && ' · já classificado'}
                  </div>
                </td>
                <td className="p-2 font-mono text-xs">{i.ncm || '—'}</td>
                <td className="p-2 font-mono text-xs text-ink-500">{i.atual_cclasstrib}</td>
                <td className="p-2 font-mono text-xs">
                  <span className={i.muda ? 'font-bold text-emerald-700' : 'text-ink-400'}>{i.sugerido_cclasstrib}</span>
                  {i.sugerido_reducao_pct ? <span className="text-ink-500"> −{i.sugerido_reducao_pct}%</span> : null}
                  {i.anexo && <div className="text-ink-400">Anexo {i.anexo}</div>}
                </td>
                <td className="p-2 text-xs text-ink-600">
                  {i.revisar && <AlertTriangle size={12} className="inline mr-1 text-amber-600" />}
                  {i.motivo}
                </td>
              </tr>
            )
          })}
          {itens.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-ink-400">Nada a sugerir.</td></tr>}
        </tbody>
      </table></div>
    </div>
  )
}

// ======================= CLASSIFICAÇÃO POR OPERAÇÃO =======================
/**
 * No Mauriti o que muda o imposto raramente é o produto — é a operação. Numa bonificação de
 * sorvete não importa que o sorvete seja tributado integralmente: a operação é não onerosa.
 *
 * A regra vencedora (menor prioridade que casar) define o CST/cClassTrib de TODOS os itens do
 * pedido e PRECEDE a classificação por produto. Cada regra mostra quanto alcança em R$ ANTES
 * de ser ligada — é o ponto: decidir com número na mão, não no escuro.
 */
function AbaOperacoes() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['reforma-operacoes-impacto'],
    queryFn: async () => (await api.get('/reforma/operacoes/impacto')).data,
  })
  const { data: ct } = useQuery<ClassTrib[]>({ queryKey: ['reforma-classtrib'], queryFn: async () => (await api.get('/reforma/classtrib')).data })

  const salvar = useMutation({
    mutationFn: async (r: any) => (await api.put(`/reforma/operacoes/${r.id}`, {
      nome: r.nome, prioridade: r.prioridade, origens: r.origens, tipos_venda: r.tipos_venda,
      condvendas: r.condvendas, modo_condicao: r.modo_condicao, cst: r.cst,
      cclasstrib: r.cclasstrib, excluir_apuracao: r.excluir_apuracao, ativo: r.ativo,
    })).data,
    onSuccess: () => {
      toast.success('Regra atualizada — a apuração já reflete')
      qc.invalidateQueries({ queryKey: ['reforma-operacoes-impacto'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  if (isLoading) return <Loading />
  const regras: any[] = data?.regras || []
  const ligadas = regras.filter(r => r.ativo)
  const alcanceLigado = ligadas.reduce((s, r) => s + (r.efeito_atual?.valor || 0), 0)

  return (
    <div className="space-y-3">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900 space-y-1">
        <div>
          A natureza da operação <strong>precede</strong> a classificação do produto: numa bonificação
          de sorvete não importa que o sorvete seja tributado integralmente — a operação é que é não
          onerosa. Vence a <strong>primeira regra ativa</strong> na ordem de prioridade.
        </div>
        <div className="pt-1">
          Base analisada: <strong>{data?.total_pedidos}</strong> pedidos faturados/entregues ·
          alcançados por regra ligada: <strong>{formatMoney(alcanceLigado)}</strong> ·
          seguem pela classificação de produto: <strong>{formatMoney(data?.sem_regra?.valor || 0)}</strong> ({data?.sem_regra?.pedidos} pedidos)
        </div>
      </div>

      <div className="card overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-left text-ink-500 border-b border-ink-200">
          <th className="p-2 w-16">Ativa</th><th className="p-2">Regra</th>
          <th className="p-2 w-36">Condições</th><th className="p-2 w-32">Classifica como</th>
          <th className="p-2 w-40 text-right">Alcance</th><th className="p-2 w-16"></th>
        </tr></thead>
        <tbody>
          {regras.map(r => <LinhaOperacao key={r.id} r={r} ct={ct || []} onSave={salvar.mutate} saving={salvar.isPending} />)}
        </tbody>
      </table></div>

      <p className="text-xs text-ink-400">
        “Alcance” é quanto a regra pegaria <strong>sozinha</strong>, mesmo desligada. “Efeito” é o que
        ela realmente classifica hoje, já descontado o que uma regra de prioridade menor levou antes.
      </p>
    </div>
  )
}

function LinhaOperacao({ r, ct, onSave, saving }: any) {
  const [row, setRow] = useState<any>({ ...r })
  const [aberto, setAberto] = useState(false)
  const dirty = row.ativo !== r.ativo || row.cclasstrib !== r.cclasstrib
    || row.prioridade !== r.prioridade || row.origens !== r.origens
    || row.tipos_venda !== r.tipos_venda || row.condvendas !== r.condvendas
    || row.modo_condicao !== r.modo_condicao
  const desc = ct.find((c: ClassTrib) => c.codigo === row.cclasstrib)?.descricao

  return (
    <>
      <tr className={`border-b border-ink-100 ${row.ativo ? '' : 'bg-ink-50/60'}`}>
        <td className="p-2">
          <button title={row.ativo ? 'Desligar regra' : 'Ligar regra'}
                  onClick={() => setRow({ ...row, ativo: !row.ativo })}
                  className={row.ativo ? 'text-emerald-600' : 'text-ink-300'}>
            <Power size={18} />
          </button>
        </td>
        <td className="p-2">
          <div className="font-medium text-ink-800">{r.nome}</div>
          <button className="text-xs text-brand-600" onClick={() => setAberto(!aberto)}>
            {aberto ? 'ocultar detalhes' : 'ver por quê / editar condições'}
          </button>
        </td>
        <td className="p-2 text-xs text-ink-500 font-mono">
          {[r.origens && `origem: ${r.origens}`, r.tipos_venda && `tipo: ${r.tipos_venda}`,
            r.condvendas && `condvenda: ${r.condvendas}`].filter(Boolean).join(` ${r.modo_condicao === 'ou' ? 'OU' : 'E'} `)}
        </td>
        <td className="p-2">
          {r.excluir_apuracao
            ? <span className="text-xs font-medium text-red-600">exclui da apuração</span>
            : (
              <select className="input py-1 text-xs font-mono" value={row.cclasstrib || ''}
                      onChange={e => setRow({ ...row, cclasstrib: e.target.value, cst: e.target.value.slice(0, 3) })}>
                {ct.map((c: ClassTrib) => <option key={c.codigo} value={c.codigo} title={c.descricao}>{c.codigo}</option>)}
              </select>
            )}
        </td>
        <td className="p-2 text-right">
          <div className="font-medium text-ink-800">{formatMoney(r.alcance_isolado?.valor || 0)}</div>
          <div className="text-xs text-ink-400">{r.alcance_isolado?.pedidos || 0} pedidos</div>
          {r.ativo && r.efeito_atual?.valor !== r.alcance_isolado?.valor && (
            <div className="text-xs text-emerald-700">efeito: {formatMoney(r.efeito_atual?.valor || 0)}</div>
          )}
        </td>
        <td className="p-2">
          {dirty && <button className="btn-primary py-1 px-2 text-xs" disabled={saving} onClick={() => onSave(row)}><Save size={13} /></button>}
        </td>
      </tr>
      {aberto && (
        <tr className="border-b border-ink-100 bg-ink-50/40">
          <td></td>
          <td colSpan={5} className="p-3 space-y-2">
            {r.observacao && <p className="text-xs text-ink-600 max-w-3xl">{r.observacao}</p>}
            {!r.excluir_apuracao && desc && (
              <p className="text-xs text-ink-500"><strong>{row.cclasstrib}</strong> — {desc}</p>
            )}
            <div className="flex gap-2 flex-wrap items-end">
              <div><label className="text-xs text-ink-500">Prioridade</label>
                <input className="input py-1 w-20 text-xs" type="number" value={row.prioridade}
                       onChange={e => setRow({ ...row, prioridade: Number(e.target.value) })} /></div>
              <div><label className="text-xs text-ink-500">Origens (CSV)</label>
                <input className="input py-1 text-xs w-56 font-mono" value={row.origens || ''}
                       onChange={e => setRow({ ...row, origens: e.target.value || null })} /></div>
              <div><label className="text-xs text-ink-500">Tipos de venda (CSV)</label>
                <input className="input py-1 text-xs w-52 font-mono" value={row.tipos_venda || ''}
                       onChange={e => setRow({ ...row, tipos_venda: e.target.value || null })} /></div>
              <div><label className="text-xs text-ink-500">condvenda (CSV)</label>
                <input className="input py-1 text-xs w-24 font-mono" value={row.condvendas || ''}
                       onChange={e => setRow({ ...row, condvendas: e.target.value || null })} /></div>
              <div><label className="text-xs text-ink-500">Combinar com</label>
                <select className="input py-1 text-xs w-20" value={row.modo_condicao || 'e'}
                        onChange={e => setRow({ ...row, modo_condicao: e.target.value })}>
                  <option value="e">E</option><option value="ou">OU</option>
                </select></div>
            </div>
            <p className="text-xs text-ink-400">
              Dentro de cada campo os valores são OU. Entre campos, o seletor acima decide.
              A transferência usa <strong>OU</strong> porque tem duas marcações que nunca aparecem juntas.
            </p>
          </td>
        </tr>
      )}
    </>
  )
}

// ======================= TABELAS OFICIAIS (CST + cClassTrib) =======================
/**
 * As tabelas de CST e cClassTrib são NACIONAIS (iguais pra todo contribuinte) e mudam a
 * cada versão do Informe Técnico 2025.002. Aqui elas podem ser conferidas, editadas à mão
 * e recarregadas direto da fonte oficial (Portal SVRS) sem precisar de deploy.
 */
function AbaTabelas() {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [filtroCst, setFiltroCst] = useState('')
  const [incluirInativos, setIncluirInativos] = useState(false)
  const [novo, setNovo] = useState<any>(null)

  const { data: cst } = useQuery<Cst[]>({ queryKey: ['reforma-cst'], queryFn: async () => (await api.get('/reforma/cst')).data })
  const { data: ct, isLoading } = useQuery<ClassTrib[]>({
    queryKey: ['reforma-classtrib-adm', busca, filtroCst, incluirInativos],
    queryFn: async () => (await api.get('/reforma/classtrib', {
      params: { busca: busca || undefined, cst: filtroCst || undefined, incluir_inativos: incluirInativos },
    })).data,
  })

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['reforma-classtrib-adm'] })
    qc.invalidateQueries({ queryKey: ['reforma-classtrib'] })
    qc.invalidateQueries({ queryKey: ['reforma-cst'] })
  }

  const atualizarOficial = useMutation({
    mutationFn: async () => (await api.post('/reforma/tabelas/atualizar-oficial')).data,
    onSuccess: (d: any) => {
      toast.success(
        `Tabela oficial recarregada — CST: ${d.cst_novos} novos / ${d.cst_atualizados} atualizados · ` +
        `cClassTrib: ${d.classtrib_novos} novos / ${d.classtrib_atualizados} atualizados`,
        { duration: 7000 },
      )
      const orfaos: string[] = d.classtrib_sem_correspondencia || []
      if (orfaos.length) {
        toast(`${orfaos.length} código(s) local(is) não vieram na tabela oficial (${orfaos.slice(0, 5).join(', ')}${orfaos.length > 5 ? '…' : ''}). Não foram apagados — confira se ainda valem.`, { duration: 10000 })
      }
      invalidar()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Não consegui atualizar do SVRS'),
  })

  const salvar = useMutation({
    mutationFn: async (r: any) => (await api.put(`/reforma/classtrib/${r.codigo}`, {
      descricao: r.descricao, reducao_pct: r.reducao_pct, permite_credito: r.permite_credito,
      ind_trib_regular: r.ind_trib_regular, permite_cred_pres: r.permite_cred_pres,
      nro_anexo: r.nro_anexo || null, ativo: r.ativo,
    })).data,
    onSuccess: () => { toast.success('cClassTrib atualizado'); invalidar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/reforma/classtrib', {
      codigo: novo.codigo, cst: novo.codigo.slice(0, 3), descricao: novo.descricao,
      reducao_pct: novo.reducao_pct === '' ? null : Number(novo.reducao_pct),
    })).data,
    onSuccess: () => { toast.success('cClassTrib criado'); setNovo(null); invalidar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar'),
  })

  const inativar = useMutation({
    mutationFn: async (codigo: string) => (await api.delete(`/reforma/classtrib/${codigo}`)).data,
    onSuccess: () => { toast.success('Código inativado'); invalidar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao inativar'),
  })

  return (
    <div className="space-y-4">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900 flex items-start gap-3">
        <div className="flex-1">
          Tabelas <strong>nacionais</strong> do Informe Técnico 2025.002 (CST e cClassTrib do IBS/CBS) — as mesmas
          pra todo contribuinte. Mudam a cada versão do IT, então dá pra recarregar direto da fonte oficial.
          Só entram aqui os códigos válidos pra <strong>NF-e</strong> e vigentes.
          <a className="inline-flex items-center gap-1 underline ml-1" target="_blank" rel="noreferrer"
             href="https://dfe-portal.svrs.rs.gov.br/DFE/ClassificacaoTributaria">
            ver no portal SVRS <ExternalLink size={11} />
          </a>
        </div>
        <button className="btn-secondary whitespace-nowrap" disabled={atualizarOficial.isPending} onClick={() => atualizarOficial.mutate()}>
          {atualizarOficial.isPending ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          Atualizar da tabela oficial
        </button>
      </div>

      {/* ---- CST ---- */}
      <div className="card"><div className="card-body">
        <h3 className="font-semibold text-ink-800 mb-2">CST do IBS/CBS <span className="text-xs text-ink-400">({cst?.length || 0} códigos)</span></h3>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-ink-500 border-b border-ink-200">
            <th className="p-1.5 w-16">Código</th><th className="p-1.5">Descrição</th>
            <th className="p-1.5 w-28">Exige tributação</th><th className="p-1.5">Indicadores</th>
          </tr></thead>
          <tbody>
            {cst?.map(c => (
              <tr key={c.codigo} className="border-b border-ink-100">
                <td className="p-1.5 font-mono font-medium">{c.codigo}</td>
                <td className="p-1.5">{c.descricao}</td>
                <td className="p-1.5">
                  {c.tributado
                    ? <span className="text-emerald-700 font-medium">sim</span>
                    : <span className="text-ink-400">não (zera o imposto)</span>}
                </td>
                <td className="p-1.5 text-xs text-ink-500">
                  {[
                    (c as any).ind_reducao_aliq && 'redução de alíquota',
                    (c as any).ind_reducao_bc && 'redução de BC',
                    (c as any).ind_monofasica && 'monofásica',
                    (c as any).ind_diferimento && 'diferimento',
                  ].filter(Boolean).join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div></div>

      {/* ---- cClassTrib ---- */}
      <div className="card"><div className="card-body space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-semibold text-ink-800">cClassTrib <span className="text-xs text-ink-400">({ct?.length || 0} códigos)</span></h3>
          <div className="relative flex-1 max-w-xs">
            <Search size={15} className="absolute left-2.5 top-2.5 text-ink-400" />
            <input className="input pl-8" placeholder="Buscar código ou descrição…" value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
          <select className="input max-w-[10rem]" value={filtroCst} onChange={e => setFiltroCst(e.target.value)}>
            <option value="">Todos os CST</option>
            {cst?.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-ink-600">
            <input type="checkbox" checked={incluirInativos} onChange={e => setIncluirInativos(e.target.checked)} /> Incluir inativos
          </label>
          <button className="btn-secondary ml-auto" onClick={() => setNovo({ codigo: '', descricao: '', reducao_pct: '' })}>
            <Plus size={15} /> Novo código
          </button>
        </div>

        {novo && (
          <div className="border border-brand-200 bg-brand-50 rounded-lg p-3 flex items-end gap-2 flex-wrap">
            <div><label className="text-xs text-ink-500">Código (6 dígitos)</label>
              <input className="input py-1 font-mono w-28" maxLength={6} value={novo.codigo}
                     onChange={e => setNovo({ ...novo, codigo: e.target.value.replace(/\D/g, '') })} /></div>
            <div className="flex-1 min-w-[14rem]"><label className="text-xs text-ink-500">Descrição</label>
              <input className="input py-1" value={novo.descricao} onChange={e => setNovo({ ...novo, descricao: e.target.value })} /></div>
            <div><label className="text-xs text-ink-500">Redução %</label>
              <input className="input py-1 w-24" type="number" step="0.01" value={novo.reducao_pct}
                     onChange={e => setNovo({ ...novo, reducao_pct: e.target.value })} /></div>
            <div className="text-xs text-ink-500 pb-2">CST = {novo.codigo.slice(0, 3) || '???'}</div>
            <button className="btn-primary py-1 px-3" disabled={novo.codigo.length !== 6 || !novo.descricao || criar.isPending} onClick={() => criar.mutate()}>
              <Save size={14} /> Criar
            </button>
            <button className="btn-secondary py-1 px-3" onClick={() => setNovo(null)}><X size={14} /></button>
          </div>
        )}

        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-ink-500 border-b border-ink-200">
            <th className="p-1.5 w-16">Código</th><th className="p-1.5 w-12">CST</th><th className="p-1.5">Descrição</th>
            <th className="p-1.5 w-24">Redução %</th><th className="p-1.5 w-16">Anexo</th>
            <th className="p-1.5 w-40">Indicadores</th><th className="p-1.5 w-24"></th>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="p-4 text-center text-ink-400"><Loader2 className="animate-spin inline" /></td></tr>}
            {ct?.map(c => <LinhaClassTrib key={c.codigo} c={c} onSave={salvar.mutate} onInativar={inativar.mutate} saving={salvar.isPending} />)}
            {ct?.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-ink-400">Nenhum código.</td></tr>}
          </tbody>
        </table></div>
      </div></div>
    </div>
  )
}

function LinhaClassTrib({ c, onSave, onInativar, saving }: any) {
  const [row, setRow] = useState<any>({ ...c })
  const dirty = row.descricao !== c.descricao
    || Number(row.reducao_pct ?? 0) !== Number(c.reducao_pct ?? 0)
    || row.nro_anexo !== c.nro_anexo
    || row.permite_credito !== c.permite_credito
  return (
    <tr className={`border-b border-ink-100 hover:bg-ink-50 ${c.ativo === false ? 'opacity-50' : ''}`}>
      <td className="p-1.5 font-mono font-medium">{c.codigo}</td>
      <td className="p-1.5 font-mono text-xs text-ink-500">{c.cst}</td>
      <td className="p-1.5">
        <input className="input py-1 text-xs w-full" value={row.descricao}
               onChange={e => setRow({ ...row, descricao: e.target.value })} />
        {c.url_legislacao && (
          <a className="text-xs text-brand-600 inline-flex items-center gap-1 mt-0.5" target="_blank" rel="noreferrer" href={c.url_legislacao}>
            LC 214 <ExternalLink size={10} />
          </a>
        )}
      </td>
      <td className="p-1.5">
        <input className="input py-1 text-xs w-20" type="number" step="0.01" value={row.reducao_pct ?? ''}
               onChange={e => setRow({ ...row, reducao_pct: e.target.value === '' ? null : Number(e.target.value) })} />
      </td>
      <td className="p-1.5">
        <input className="input py-1 text-xs w-14" value={row.nro_anexo ?? ''}
               onChange={e => setRow({ ...row, nro_anexo: e.target.value || null })} />
      </td>
      <td className="p-1.5 text-xs text-ink-500">
        {[
          c.ind_trib_regular && 'exige gTribRegular',
          c.permite_cred_pres && 'crédito presumido',
          c.permite_credito === false && 'não gera crédito',
        ].filter(Boolean).join(' · ') || '—'}
      </td>
      <td className="p-1.5 whitespace-nowrap">
        {dirty && <button className="btn-primary py-1 px-2 text-xs" disabled={saving} onClick={() => onSave(row)}><Save size={13} /></button>}
        {c.ativo !== false && !dirty && (
          <button className="text-ink-400 hover:text-red-500" title="Inativar código" onClick={() => onInativar(c.codigo)}>
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  )
}

// ======================= SIMULADOR =======================
function AbaSimulador() {
  const [itens, setItens] = useState<any[]>([{ valor: 1000, cst: '', cclasstrib: '', reducao_pct: '', is_pct: '', descricao: '' }])
  const [res, setRes] = useState<any>(null)
  const { data: cst } = useQuery<Cst[]>({ queryKey: ['reforma-cst'], queryFn: async () => (await api.get('/reforma/cst')).data })

  const simular = useMutation({
    mutationFn: async () => (await api.post('/reforma/simular', {
      itens: itens.map(i => ({
        valor: Number(i.valor) || 0,
        cst: i.cst || undefined, cclasstrib: i.cclasstrib || undefined,
        reducao_pct: i.reducao_pct === '' ? undefined : Number(i.reducao_pct),
        is_pct: i.is_pct === '' ? undefined : Number(i.is_pct),
        descricao: i.descricao || undefined,
      })),
    })).data,
    onSuccess: (d) => setRes(d),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao simular'),
  })

  const upd = (idx: number, k: string, v: any) => setItens(itens.map((it, i) => i === idx ? { ...it, [k]: v } : it))

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card"><div className="card-body space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-ink-800">Itens</h3>
          <button className="btn-secondary py-1 px-2 text-xs" onClick={() => setItens([...itens, { valor: 0, cst: '', cclasstrib: '', reducao_pct: '', is_pct: '', descricao: '' }])}><Plus size={13} /> Item</button>
        </div>
        {itens.map((it, idx) => (
          <div key={idx} className="border border-ink-200 rounded-lg p-2 space-y-2">
            <div className="flex gap-2">
              <input className="input flex-1" placeholder="Descrição (opcional)" value={it.descricao} onChange={e => upd(idx, 'descricao', e.target.value)} />
              {itens.length > 1 && <button className="text-red-500" onClick={() => setItens(itens.filter((_, i) => i !== idx))}><Trash2 size={16} /></button>}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><label className="text-xs text-ink-500">Valor</label><input className="input py-1" type="number" step="0.01" value={it.valor} onChange={e => upd(idx, 'valor', e.target.value)} /></div>
              <div><label className="text-xs text-ink-500">CST</label>
                <select className="input py-1 text-xs font-mono" value={it.cst} onChange={e => upd(idx, 'cst', e.target.value)}>
                  <option value="">padrão</option>
                  {cst?.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-ink-500">Redução %</label><input className="input py-1" type="number" step="0.01" value={it.reducao_pct} onChange={e => upd(idx, 'reducao_pct', e.target.value)} /></div>
              <div><label className="text-xs text-ink-500">IS %</label><input className="input py-1" type="number" step="0.01" value={it.is_pct} onChange={e => upd(idx, 'is_pct', e.target.value)} /></div>
            </div>
          </div>
        ))}
        <button className="btn-primary w-full" disabled={simular.isPending} onClick={() => simular.mutate()}><Calculator size={15} /> Calcular</button>
      </div></div>

      <div className="card"><div className="card-body">
        <h3 className="font-semibold text-ink-800 mb-2">Resultado</h3>
        {!res && <p className="text-sm text-ink-400">Informe os itens e clique em Calcular.</p>}
        {res && <>
          <table className="w-full text-sm mb-3">
            <thead><tr className="text-left text-ink-500 border-b"><th className="py-1">Item</th><th className="py-1 text-right">CBS</th><th className="py-1 text-right">IBS</th><th className="py-1 text-right">IS</th><th className="py-1 text-right">Total</th></tr></thead>
            <tbody>
              {res.itens.map((i: any, k: number) => (
                <tr key={k} className="border-b border-ink-100">
                  <td className="py-1">{i.descricao || `${i.cst}/${i.cclasstrib}`}</td>
                  <td className="py-1 text-right">{formatMoney(i.v_cbs)}</td>
                  <td className="py-1 text-right">{formatMoney(i.v_ibs)}</td>
                  <td className="py-1 text-right">{formatMoney(i.v_is)}</td>
                  <td className="py-1 text-right font-medium">{formatMoney(i.v_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bg-brand-50 rounded-lg p-3 space-y-1 text-sm">
            <Row label="Valor das operações" v={res.totais.valor} />
            <Row label="Base de cálculo" v={res.totais.base} />
            <Row label="CBS" v={res.totais.v_cbs} />
            <Row label="IBS (UF + Mun.)" v={res.totais.v_ibs} />
            <Row label="IS" v={res.totais.v_is} />
            <div className="border-t border-brand-200 pt-1"><Row label="Total de tributos" v={res.totais.v_total} bold /></div>
          </div>
        </>}
      </div></div>
    </div>
  )
}

// ======================= APURAÇÃO =======================
function AbaApuracao() {
  const hoje = new Date()
  const ini = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`
  const fim = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).getDate()).padStart(2, '0')}`
  const [inicio, setInicio] = useState(ini)
  const [fimD, setFimD] = useState(fim)
  const [res, setRes] = useState<any>(null)

  const apurar = useMutation({
    mutationFn: async () => (await api.get('/reforma/apuracao', { params: { inicio, fim: fimD } })).data,
    onSuccess: (d) => setRes(d),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro na apuração'),
  })

  return (
    <div className="space-y-4">
      <div className="card"><div className="card-body flex items-end gap-3 flex-wrap">
        <div><label className="label">Início</label><input className="input" type="date" value={inicio} onChange={e => setInicio(e.target.value)} /></div>
        <div><label className="label">Fim</label><input className="input" type="date" value={fimD} onChange={e => setFimD(e.target.value)} /></div>
        <button className="btn-primary" disabled={apurar.isPending} onClick={() => apurar.mutate()}>{apurar.isPending ? <Loader2 size={15} className="animate-spin" /> : <FileBarChart size={15} />} Apurar</button>
        {res && <a className="btn-secondary" href={`/api/reforma/apuracao?inicio=${inicio}&fim=${fimD}&pdf=true`} target="_blank" rel="noreferrer">Baixar PDF</a>}
      </div></div>
      {/* [30/07] A competência passou a ser a data de EMISSÃO da nota (antes era a data de
          criação do pedido, que jogava no mês errado quem foi faturado no mês seguinte). */}
      {res?.competencia && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900">
          Competência pela <strong>data de emissão da nota</strong>; sem nota, pela data do pedido.
          Neste período: <strong>{res.competencia.por_data_da_nota}</strong> pedido(s) pela nota ·{' '}
          <strong>{res.competencia.por_data_do_pedido}</strong> sem nota (data do pedido)
          {res.competencia.fora_por_nota_cancelada?.pedidos > 0 && (
            <> · <strong>{res.competencia.fora_por_nota_cancelada.pedidos}</strong> fora da base por
            nota cancelada ({formatMoney(res.competencia.fora_por_nota_cancelada.valor)})</>
          )}
        </div>
      )}
      {res && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card"><div className="card-body">
            <h3 className="font-semibold text-ink-800 mb-2">Tributos apurados <span className="text-xs text-ink-400">({res.pedidos} pedidos)</span></h3>
            <div className="space-y-1 text-sm">
              <Row label="Valor das operações" v={res.totais.valor} />
              <Row label="Base de cálculo" v={res.totais.base} />
              <Row label="CBS" v={res.totais.v_cbs} />
              <Row label="IBS estadual" v={res.totais.v_ibs_uf} />
              <Row label="IBS municipal" v={res.totais.v_ibs_mun} />
              <Row label="Imposto Seletivo" v={res.totais.v_is} />
              <div className="border-t border-ink-200 pt-1"><Row label="Total de tributos" v={res.totais.v_total} bold /></div>
            </div>
          </div></div>
          <div className="card"><div className="card-body">
            <h3 className="font-semibold text-ink-800 mb-2">Compensação PIS/COFINS (2026)</h3>
            <div className="space-y-1 text-sm">
              <Row label="PIS/COFINS estimado" v={res.pis_cofins_estimado} />
              <Row label="CBS compensável" v={res.cbs_compensavel_pis_cofins} />
              <div className="border-t border-ink-200 pt-1"><Row label="Saldo de CBS a recolher" v={res.saldo_cbs_a_recolher} bold /></div>
            </div>
            <p className="text-xs text-ink-400 mt-3">Em 2026 a CBS de teste pode ser compensada com PIS/COFINS do mesmo período — sem carga extra.</p>
          </div></div>

          {/* [30/07] Quebra por natureza da operação — o contador precisa ver o que entrou
              como venda, o que entrou como não onerosa e o que saiu da base. */}
          {res.por_natureza?.length > 0 && (
            <div className="card md:col-span-2"><div className="card-body">
              <h3 className="font-semibold text-ink-800 mb-2">Por natureza da operação</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-ink-500 border-b">
                  <th className="py-1">Natureza</th><th className="py-1 w-28">cClassTrib</th>
                  <th className="py-1 text-right w-24">Pedidos</th>
                  <th className="py-1 text-right w-36">Valor</th>
                  <th className="py-1 text-right w-36">Tributos</th>
                </tr></thead>
                <tbody>
                  {res.por_natureza.map((n: any) => (
                    <tr key={n.natureza} className="border-b border-ink-100">
                      <td className="py-1">{n.natureza}</td>
                      <td className="py-1 font-mono text-xs">{n.cclasstrib || '—'}</td>
                      <td className="py-1 text-right">{n.pedidos}</td>
                      <td className="py-1 text-right">{formatMoney(n.valor)}</td>
                      <td className="py-1 text-right font-medium">{formatMoney(n.tributos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {res.excluidos?.pedidos > 0 && (
                <p className="text-xs text-ink-500 mt-2">
                  Fora da base: <strong>{res.excluidos.pedidos}</strong> pedido(s) ·{' '}
                  <strong>{formatMoney(res.excluidos.valor)}</strong>
                  {' '}({Object.entries(res.excluidos.por_regra || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')})
                </p>
              )}
            </div></div>
          )}
        </div>
      )}
    </div>
  )
}

// ======================= helpers UI =======================
function NumField({ label, value, onChange }: { label: string; value: any; onChange: (v: string) => void }) {
  return <div><label className="label">{label}</label><input className="input font-mono" type="number" step="0.0001" value={value} onChange={e => onChange(e.target.value)} /></div>
}
function Row({ label, v, bold }: { label: string; v: number; bold?: boolean }) {
  return <div className={`flex justify-between ${bold ? 'font-bold text-ink-900' : 'text-ink-600'}`}><span>{label}</span><span>{formatMoney(v)}</span></div>
}
function Loading() { return <div className="p-8 text-center text-ink-400"><Loader2 className="animate-spin inline" /> Carregando…</div> }
